<script>
import { prospectComputed } from '@state/helpers';
export default {
  components: {},
  props: {},
  mounted() {},
  data() {
    return {};
  },
  computed: {
    ...prospectComputed,
    acquisitionSteps() {
      return [
        {
          order: 1,
          title: 'Build your lead list',
          btnText: 'Connect Gmail',
          coverPhoto: require('@assets/images/campaign-step-1.jpg'),
          complete: this.allProspects.length > 0,
        },
        {
          order: 2,
          title: 'Send Campaign',
          btnText: 'Start Campaign',
          complete: this.numOfProspectsContacted > 0,
          coverPhoto: require('@assets/images/campaign-step-2.jpg'),
        },
        {
          order: 3,
          title: 'Create a Lead',
          btnText: 'Select a lead to convert them to an active client',
          coverPhoto: require('@assets/images/campaign-step-3.jpg'),
          complete:
            this.numOfProspectsContacted > 0 &&
            this.allProspects.some((prospect) => !!prospect.is_converted),
        },
      ];
    },
    numOfProspectsContacted() {
      return (
        this.allProspects.length > 0 &&
        this.allProspects.reduce(
          (sum, { texts, emails }) =>
            texts.length > 0 || emails.length > 0 ? sum + 1 : sum,
          0
        )
      );
    },
    currentStep() {
      // first false is the current step
      const firstFalse = this.acquisitionSteps.findIndex(
        (step) => !step.complete
      );
      // if all true, current step is 3 not -1
      return firstFalse === -1 ? this.acquisitionSteps.length : firstFalse;
    },
  },
  methods: {
    handleClick(stepOrder) {
      const actions = {
        '1': () => {
          this.$bvModal.show('import-wizard-modal');
        },
        '2': () => {
          this.$emit('send-invite');
        },
        otherCases: () => null,
      };
      return (actions[stepOrder] || actions.otherCases)();
    },
  },
};
</script>

<template>
  <div class="prospect-client-acquisition-steps">
    <div
      :class="[
        'acquisition-step-container',
        {
          complete: step.complete,
          'current-step': i === currentStep,
          'future-step': i > currentStep,
        },
      ]"
      v-for="(step, i) in acquisitionSteps"
      :key="step.order"
    >
      <div class="step-order">
        {{ step.order }}
      </div>
      <div class="card-client-acquisition-step">
        <div class="acquisition-step-wrapper">
          <div
            class="card-hero"
            v-lazy:background-image="i <= currentStep ? step.coverPhoto : null"
          />
          <div class="card-content">
            <label>Step {{ step.order }}</label>
            <h5>{{ step.title }}</h5>
            <span class="card-action">
              <div class="icon-complete" v-if="step.complete">
                <img src="@assets/images/icon-check-thin.svg" />
              </div>
              <template v-if="!step.complete && i <= currentStep">
                <p class="step-btn-text" v-if="step.order === 3">
                  {{ step.btnText }}
                </p>
                <BaseButton
                  type="button"
                  class="btn-primary btn-round"
                  v-else
                  @click.stop="handleClick(step.order)"
                >
                  {{ step.btnText }}
                </BaseButton>
              </template>
            </span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import '@design';

.prospect-client-acquisition-steps {
  display: flex;
  margin-bottom: 2rem;
  width: 100%;
}

.acquisition-step-container {
  background-color: white;
  border: 1px solid $color-light-gray-four;
  border-radius: $base-border-radius-lg;
  box-sizing: border-box;
  height: 21.6rem;
  position: relative;
  width: 17.3rem;

  &:not(:last-child) {
    margin-right: 3.2rem;
  }

  &:hover:not(.complete):not(.future-step) {
    box-shadow: 0 0.4rem 0.8rem rgba($color-black-wo-rgb, 0.1);
    cursor: pointer;
    transform: scale(1.01);
  }

  .step-order {
    align-items: center;
    background-color: white;
    border-bottom-right-radius: $base-border-radius-lg;
    border-top-left-radius: $base-border-radius-md;
    color: $color-action;
    display: flex;
    font-size: 3rem;
    font-style: italic;
    font-weight: 900;
    height: 5.4rem;
    justify-content: center;
    left: 1rem;
    letter-spacing: -0.233333px;
    line-height: 3.4rem;
    position: absolute;
    text-align: center;
    top: 1rem;
    width: 5rem;
    z-index: 1;
  }

  .card-client-acquisition-step {
    border: 10px solid white;
    border-radius: $base-border-radius-lg;
    display: flex;
    height: 100%;
    justify-content: center;
    position: relative;
    transition: transform $duration-animation-base - 75 ease-in-out,
      box-shadow $duration-animation-base - 50 ease-in-out;

    .acquisition-step-wrapper {
      height: 21.4rem;
      width: 100%;

      .card-hero {
        align-items: flex-start;
        background: $color-light-gray-three; // fallback if no image
        background-position: center;
        background-repeat: no-repeat;
        background-size: cover;
        border-radius: $base-border-radius-md;
        display: flex;
        height: 50%;
        margin-bottom: 0.5rem;
        width: 100%;
      }

      .card-content {
        align-items: center;
        display: flex;
        flex-direction: column;

        label {
          font-size: 0.9rem;
          line-height: 2.2rem;
          margin-bottom: 0;
          opacity: 0.5;
        }

        h5 {
          font-size: 1.4rem;
        }

        .card-action {
          .icon-complete {
            align-items: center;
            border: 1px solid $color-primary;
            border-radius: $border-radius-circle;
            display: flex;
            height: 1.8rem;
            justify-content: center;
            width: 1.8rem;
          }

          .step-btn-text {
            line-height: 1.3;
            margin-bottom: 0;
            padding: 0 1rem;
            text-align: center;
          }
        }
      }
    }
  }

  &.complete {
    border-color: $color-off-white-one;

    &::after {
      background-color: $color-off-white-one;
      border-radius: $base-border-radius-lg;
      bottom: 0;
      content: '';
      height: 21.6rem;
      left: 0;
      opacity: 0.4;
      position: absolute;
      right: 0;
      top: 0;
      width: 17.3rem;
      z-index: 1;
    }
  }

  &.current-step {
    border-radius: $base-border-radius-lg + 0.8rem;
    height: 35.2rem;
    width: 27.6rem;

    .step-order {
      border-bottom-right-radius: $base-border-radius-lg + 0.8rem;
      font-size: 4.5rem;
      height: 8.8rem;
      width: 6.6rem;
    }

    .card-client-acquisition-step {
      border-radius: $base-border-radius-lg + 0.8rem;
    }

    .acquisition-step-wrapper {
      position: relative;
      .card-hero {
        height: 100%;
        margin-bottom: 1.5rem;
        &::after {
          background: rgba($color-primary-one-wo-rgb, 0.8);
          border-radius: $base-border-radius-md;
          bottom: 0;
          content: '';
          left: 0;
          mix-blend-mode: multiply;
          position: absolute;
          right: 0;
          top: 0;
        }
      }

      .card-content {
        label {
          font-size: 1.2rem;
          margin-bottom: 0.4rem;
        }

        h5 {
          font-size: 2.2rem;
          margin-bottom: 1.6rem;
        }
      }
    }
  }
  &.future-step {
    background-color: $color-off-white-one;
    border: 1px solid $color-light-gray-three;
    .card-client-acquisition-step {
      border: 10px solid $color-off-white-one;
      .acquisition-step-wrapper {
        .card-content {
          h5 {
            color: $color-medium-gray-one;
          }
        }
      }
    }
    .step-order {
      background-color: $color-off-white-one;
      color: $color-light-gray-seven;
    }
  }
}
</style>
